import {
  AlignItems,
  Body2,
  Bold,
  Box,
  Color,
  Display,
  FlexDirection,
  FontFamily,
  Heading2,
  Icon,
  IconVariant,
  Join,
  JustifyContent,
  Overflow,
  Space,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import React from 'react'
import { CSSObject } from '@emotion/react'

import { OUR_SUPPLIERS_COPY } from '../../config'
import { LandingPageContentSections, TrackingActions, trackCTAClick } from '../../tracking'
import { InlineLink } from '../InlineLink/InlineLink'
import { CWCLinkButton } from '../CWCLinkButton/CWCLinkButton'
import { CWCSupplierCarousel } from './components/CWCSupplierCarousel/CWCSupplierCarousel'

// Full-bleed background band: Porcelain / grey-95 (#F2F3F4) — no Citrus Color token, so raw hex.
// Vertical padding lives here (so the grey extends top/bottom); it centres the capped inner row so
// ultra-wide screens don't left-align the content. Matches the platform-standard vertical bands used
// across the CwC homepage: 56 / 56 / 56 / 80.
const SectionContainer = withCitrusTheme(Box, (theme) => [
  { backgroundColor: '#F2F3F4' } as CSSObject,
  theme.responsiveStyle(['56px', null, null, '80px'], ['paddingTop', 'paddingBottom']) as CSSObject,
])

// Gutters live on each child, not the row (mirrors CWCWeeklyCollectionsSection, PR #12305), so
// the carousel column can bleed to the page edge at every breakpoint. Desktop left inset grows
// above 1920px (`max(128px, (100vw − 1920)/2 + 128px)`) to keep the text's centred-1920 margin.
const SectionRow = withCitrusTheme(Box, (theme) => [
  { width: '100%' } as CSSObject,
  theme.responsiveStyle(
    ['0px', '0px', '0px', 'max(128px, calc((100vw - 1920px) / 2 + 128px))'],
    ['paddingLeft'],
  ) as CSSObject,
  theme.responsiveStyle(['0px', '0px', '0px', '128px'], ['gap']) as CSSObject,
])

// Left gutter only, matching the content column — no right padding, so the carousel bleeds.
const CarouselColumn = withCitrusTheme(
  Box,
  (theme) => theme.responsiveStyle(['24px', '24px', '40px', '0px'], ['paddingLeft']) as CSSObject,
)

// Centres the CTA below the carousel (stacked layout only) — matches
// CWCWeeklyCollectionsSection's StackedCta; the button is a fixed 345px inline element.
const StackedCta = withCitrusTheme(Box, () => ({ textAlign: 'center' } as CSSObject))

// "Quality you can taste" — Axiforma ExtraBold 36/40, ink #1A2026 (both bespoke, set here).
// Full-length responsiveStyle arrays (one value per breakpoint) so 36/40 wins at ≥768 too — a
// single-value array leaves Heading2's own @media default (28px) to win on desktop.
const SectionHeading = withCitrusTheme(Heading2, (theme) => [
  { color: '#1A2026' },
  theme.responsiveStyle(['36px', '36px', '36px'], ['fontSize']),
  theme.responsiveStyle(['40px', '40px', '40px'], ['lineHeight']),
])

// Bullet copy: 16/24, text/primary (#333D47 = ColdGrey_800).
const BulletText = withCitrusTheme(Body2, (theme) => [
  theme.responsiveStyle(['16px', '16px', '16px'], ['fontSize']),
  theme.responsiveStyle(['24px', '24px', '24px'], ['lineHeight']),
])

// Forces "the UK" onto its own line so "UK" is never orphaned alone — matches Figma's mobile and
// desktop frames (both hard-code this break); the tablet frame's wider column doesn't need it.
const ForcedBreak = withCitrusTheme(
  'br',
  (theme) =>
    theme.responsiveStyle(['inline', 'inline', 'none', 'inline'], ['display']) as CSSObject,
)

function SupplierBullet({ children }: { children: React.ReactNode }) {
  return (
    <Box display={Display.Flex} flexDirection={FlexDirection.Row} alignItems={AlignItems.FlexStart}>
      {/* #008610 (Figma Success/FS-800) has no matching Citrus colour token — raw hex, set via style. */}
      <Box style={{ color: '#008610' }}>
        <Icon name="tick" variant={IconVariant.Inherit} />
      </Box>
      <Space direction="horizontal" size={2} />
      <BulletText color={Color.ColdGrey_800}>{children}</BulletText>
    </Box>
  )
}

/**
 * CwC "Quality you can taste" suppliers section. A fork of `OurSuppliersSection` — disposable if
 * the CwC experiment loses, and it keeps Control / RAF / paid-search untouched. Two-column only at
 * Citrus's ≥1024px breakpoint (content left, carousel right); single column stacked below it,
 * matching `CWCWeeklyCollectionsSection`'s breakpoint and right-bleed layout. The CTA renders in
 * two responsive-visibility spots (content column on the wide layout, after the carousel on the
 * stacked layout) — the established `OurSuppliersSection` idiom.
 */
export function CWCSuppliersSection() {
  const trackCtaClick = () => {
    trackCTAClick(TrackingActions.OUR_SUPPLIERS, LandingPageContentSections.OUR_SUPPLIERS)
  }

  const cta = (
    <CWCLinkButton
      href={OUR_SUPPLIERS_COPY.ourSuppliersURL}
      variant="secondary"
      onClick={trackCtaClick}
    >
      {OUR_SUPPLIERS_COPY.ctaLabel}
    </CWCLinkButton>
  )

  return (
    <section data-testid="cwcSuppliersSection">
      <SectionContainer display={Display.Flex} justifyContent={JustifyContent.Center}>
        <SectionRow
          display={Display.Flex}
          flexDirection={[
            FlexDirection.Column,
            FlexDirection.Column,
            FlexDirection.Column,
            FlexDirection.Row,
          ]}
        >
          {/* Content column — heading, bullets, and (wide layout only) the CTA. Fixed 350px on the
            wide layout (design 408:888) so the heading wraps to two lines and the carousel fills
            the rest. `flexShrink 0` keeps it from being squeezed by the growing carousel. */}
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            alignItems={AlignItems.FlexStart}
            flexShrink={0}
            paddingH={[6, 6, 10, 0]}
            width={['100%', null, null, '350px']}
          >
            <SectionHeading fontFamily={FontFamily.ExtraBold}>
              Quality you <ForcedBreak />
              can taste
            </SectionHeading>
            <Space size={6} />
            <Join with={<Space size={4} />}>
              <SupplierBullet>
                <Bold>100% British fresh meat</Bold> with chicken, beef, pork and lamb raised here
                in <ForcedBreak />
                the UK
              </SupplierBullet>
              <SupplierBullet>
                <Bold>Hand-selected seasonal produce</Bold> from trusted farms in West Sussex,
                Cornwall and beyond
              </SupplierBullet>
              <SupplierBullet>
                <Bold>
                  Proud to be a{' '}
                  <InlineLink backgroundStyle="light" href={OUR_SUPPLIERS_COPY.sustainabilityURL}>
                    B Corp™
                  </InlineLink>
                </Bold>
                , meeting the highest environmental and social standards
              </SupplierBullet>
            </Join>
            <Space size={8} />
            <Box display={[Display.None, Display.None, Display.None, Display.Block]} width="100%">
              {cta}
            </Box>
          </Box>

          {/* Carousel column — the supplier carousel and (stacked layout only) the CTA. `minWidth 0`
            lets the embla carousel's horizontal overflow work inside the flex child. */}
          <CarouselColumn
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            alignItems={AlignItems.Center}
            justifyContent={JustifyContent.Center}
            flexGrow={1}
            minWidth="0"
            overflowX={Overflow.Hidden}
            width={['100%', null, null, 'auto']}
          >
            <CWCSupplierCarousel />
            {/* Own right padding — unlike the carousel, the CTA shouldn't bleed. */}
            <StackedCta
              display={[Display.Block, Display.Block, Display.Block, Display.None]}
              width="100%"
              paddingRight={[6, 6, 10]}
            >
              <Space size={8} />
              {cta}
            </StackedCta>
          </CarouselColumn>
        </SectionRow>
      </SectionContainer>
    </section>
  )
}
